import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AddIcon,
  Button,
  Checkbox,
  CloudIcon,
  DisableIcon,
  IconButton,
  InlineAlert,
  Pane,
  RefreshIcon,
  SearchInput,
  Select,
  TickCircleIcon,
  toaster,
  TrashIcon,
} from "evergreen-ui";
import DataTable from "../../components/DataTable";
import PromiseButton from "../../components/PromiseButton";
import AccountingContext from "../../contexts/accounting";
import ControlHubContext from "../../contexts/controlhub";
import useControlHub from "../../hooks/useControlHub";
import useDebounce from "../../hooks/useDebounce";
import VendorFormDialog from "../../components/VendorFormDialog";
import VendorDeleteDialog from "../../components/VendorDeleteDialog";
import VendorsDeleteDialog from "../../components/VendorsDeleteDialog";
import BlockingDialog from "../../components/BlockingDialog";
import { INITIAL_BLOCKING_VALUES } from "../../utils/constants";
import { FREE_PLAN_NAME } from "../../config";

const PAYMENT_MODE_LABELS = {
  po: "ACH / eCheck",
  cc: "Credit Card",
};
const PERMISSIONS = {
  PO_SELF_ALL_BILLS_ADMIN: "po_self_all_bills_admin",
  PO_ALL_BILLS_ADMIN: "po_all_bills_admin",
};
const AccountingVendorsScreen = () => {
  const { api } = useControlHub();
  const { system, SYSTEMS_NAME_MAP } = useContext(AccountingContext);
  const { currentCompanyPlan } = useContext(ControlHubContext);
  const [source, setSource] = useState();
  const [data, setData] = useState();
  const [dialog, setDialog] = useState(null);
  const [openBlockingDialog, setOpenBlockingDialog] = useState(false);
  const [blockingData, setBlockingData] = useState(INITIAL_BLOCKING_VALUES);
  const [userData, setUserData] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [selected, setSelected] = useState({});

  const loadVendors = useCallback(() => {
    api
      .getVendors({ preferred: false })
      .then((data) => {
        setSource(data);
        setSelected({});
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { answer, success, message } = await api.getCurrentUser();
        if (!success) {
          throw new Error(message);
        }
        setUserData(answer?.iam);
      } catch (error) {
        toaster.danger(error?.message || "Unable to retrieve user data");
      }
    };
    api?.isReady && fetchUser();
  }, [api]);

  useEffect(() => {
    if (!source) {
      return;
    }
    let _data = [...source];
    if (filter) {
      switch (filter) {
        case "synced":
          _data = _data.filter(
            (vendor) => vendor["external_sync_status"] === filter
          );
          break;
        case "controlhub":
          _data = _data.filter(
            (vendor) =>
              !vendor["external_sync_status"] ||
              vendor["external_sync_status"] === "dirty"
          );
          break;
        default:
          break;
      }
    }
    if (debouncedSearch) {
      _data = _data.filter((vendor) =>
        vendor.name.toUpperCase().includes(debouncedSearch.toUpperCase())
      );
    }
    setData(_data);
  }, [debouncedSearch, filter, source]);

  const permissionsAndFeatures = useCallback(
    ({ type = "" }) => {
      switch (type) {
        case PERMISSIONS.PO_SELF_ALL_BILLS_ADMIN:
          return {
            feature: currentCompanyPlan !== FREE_PLAN_NAME,
            permission:
              userData?.purchaser_po_self ||
              userData?.purchaser_po_all ||
              userData?.bills ||
              userData?.admin,
          };
        case PERMISSIONS.PO_ALL_BILLS_ADMIN:
          return {
            feature: currentCompanyPlan !== FREE_PLAN_NAME,
            permission:
              userData?.purchaser_po_all || userData?.bills || userData?.admin,
          };
        default:
          return {
            feature: false,
            permission: false,
          };
      }
    },
    [userData, currentCompanyPlan]
  );

  const hasPermissionsAndFeatures = useCallback(
    ({ type = "" }) => {
      const { feature, permission } = permissionsAndFeatures({ type });
      if (!feature || !permission) {
        setBlockingData({
          hasFeature: feature,
          hasPermission: permission,
        });
        setOpenBlockingDialog(true);
        return false;
      }
      return true;
    },
    [permissionsAndFeatures]
  );

  const handleFilterChange = useCallback((event) => {
    setFilter(event.target.value);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
  }, []);

  const handleSelectChange = useCallback(
    (vendor) =>
      ({ target: { checked } }) => {
        if (checked) {
          setSelected({ ...selected, [vendor.id]: vendor });
        } else if (vendor.id in selected) {
          const { [vendor.id]: _, ..._selected } = selected;
          setSelected(_selected);
        }
      },
    [selected]
  );

  const handleSelectClick = useCallback((event) => event.stopPropagation(), []);

  const handleSelectedVendorsDelete = useCallback(() => {
    const handleDialogClose = () => {
      setDialog(null);
    };

    const handleDialogFailure = () => {
      loadVendors();
      setDialog(null);
    };

    const handleDialogSuccess = () => {
      loadVendors();
      setDialog(null);
    };

    const hasPAndF = hasPermissionsAndFeatures({
      type: PERMISSIONS.PO_ALL_BILLS_ADMIN,
    });
    if (!hasPAndF) return;

    setDialog(
      <VendorsDeleteDialog
        onClose={handleDialogClose}
        onFailure={handleDialogFailure}
        onSuccess={handleDialogSuccess}
        vendors={Object.values(selected)}
      />
    );
  }, [loadVendors, selected, hasPermissionsAndFeatures]);

  const handleVendorDelete = useCallback(
    (vendor) => (event) => {
      event.stopPropagation();

      const handleDialogClose = () => {
        setDialog(null);
      };

      const handleDialogFailure = () => {
        setDialog(null);
      };

      const handleDialogSuccess = () => {
        loadVendors();
        setDialog(null);
      };

      const hasPAndF = hasPermissionsAndFeatures({
        type: PERMISSIONS.PO_ALL_BILLS_ADMIN,
      });
      if (!hasPAndF) return;

      setDialog(
        <VendorDeleteDialog
          onClose={handleDialogClose}
          onFailure={handleDialogFailure}
          onSuccess={handleDialogSuccess}
          vendor={vendor}
        />
      );
    },
    [loadVendors, hasPermissionsAndFeatures]
  );

  const showVendorForm = useCallback(
    (vendor) => {
      const handleDialogCancel = () => {
        setDialog(null);
      };

      const handleDialogClose = () => {
        setDialog(null);
      };

      const handleDialogConfirm = () => {
        loadVendors();
        setDialog(null);
      };

      const hasPAndF = hasPermissionsAndFeatures({
        type: PERMISSIONS.PO_ALL_BILLS_ADMIN,
      });
      if (!hasPAndF) return;

      setDialog(
        <VendorFormDialog
          isShown={true}
          onCancel={handleDialogCancel}
          onCloseComplete={handleDialogClose}
          onConfirm={handleDialogConfirm}
          vendorID={vendor?.["id"]}
        />
      );
    },
    [loadVendors, hasPermissionsAndFeatures]
  );

  const handleCloseBlockingDialog = useCallback(() => {
    setBlockingData(INITIAL_BLOCKING_VALUES);
    setOpenBlockingDialog(false);
  }, []);

  const handleAddNewVendor = useCallback(() => {
    const hasPAndF = hasPermissionsAndFeatures({
      type: PERMISSIONS.PO_SELF_ALL_BILLS_ADMIN,
    });
    if (!hasPAndF) return;
    showVendorForm();
  }, [showVendorForm, hasPermissionsAndFeatures]);

  const getSyncPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!system) {
        reject();
        return;
      }

      api
        .syncAccountingSystem({ systemName: system.name })
        .then((data) => {
          if (!data.success) {
            throw new Error(
              `${SYSTEMS_NAME_MAP[system.name]} Sync couldn't be completed: ${
                data.message || "-"
              }`
            );
          }
          toaster.success(
            `${SYSTEMS_NAME_MAP[system.name]} Sync was completed successfully`
          );
          resolve();
        })
        .catch((error) => {
          if (error) {
            toaster.danger(error.message);
          }
          reject();
        });
    });
  }, [SYSTEMS_NAME_MAP, api, system]);

  const tableColumns = useMemo(
    () => [
      {
        flex: "0 0 64px",
        render: (vendor) => (
          <Checkbox
            checked={vendor.id in selected}
            onChange={handleSelectChange(vendor)}
            onClick={handleSelectClick}
          />
        ),
      },
      {
        key: "external_sync_status",
        name: "Sync",
        flex: "0 0 72px",
        render: (vendor) =>
          vendor["external_sync_status"] === "synced" && system ? (
            <CloudIcon title={`Sync'd with ${SYSTEMS_NAME_MAP[system.name]}`} />
          ) : null,
      },
      {
        key: "name",
        name: "Vendor",
      },
      {
        name: "Payment Info",
        render: (vendor) => (
          <Pane alignItems="center" display="flex">
            {vendor["name"] && vendor["billing_email"] ? (
              <>
                <TickCircleIcon marginRight={8} />
                Complete
              </>
            ) : (
              <>
                <Pane alignItems="center" display="flex">
                  <DisableIcon marginRight={8} />
                  Incomplete
                </Pane>
              </>
            )}
            <Pane marginLeft={4}>
              {vendor["payment_mode"] &&
                `(${PAYMENT_MODE_LABELS[vendor["payment_mode"]]})`}
            </Pane>
          </Pane>
        ),
      },
      {
        actions: true,
        flex: "0 0 64px",
        render: (vendor) => (
          <IconButton
            appearance="minimal"
            aria-label="Void Payment"
            icon={TrashIcon}
            onClick={handleVendorDelete(vendor)}
          />
        ),
      },
    ],
    [
      SYSTEMS_NAME_MAP,
      handleSelectChange,
      handleSelectClick,
      handleVendorDelete,
      selected,
      system,
    ]
  );

  const handleRowSelect = useCallback(
    (vendor) => {
      showVendorForm(vendor);
    },
    [showVendorForm]
  );

  return (
    <Pane>
      {system?.active && system?.integrated ? (
        <InlineAlert size={300}>
          {`New vendors sync with ${
            SYSTEMS_NAME_MAP[system.name]
          } only after a PO is created or a Bill is paid`}
        </InlineAlert>
      ) : null}
      <Pane
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        marginY={16}
      >
        <Pane display="flex" marginY={8}>
          <SearchInput
            marginRight={16}
            onChange={handleSearchChange}
            placeholder="Search Vendors..."
            value={search}
          />
          <Select marginRight={16} onChange={handleFilterChange} value={filter}>
            <option value="">All Vendors</option>
            {system?.active && system?.integrated ? (
              <option value="synced">{`Synced with ${
                SYSTEMS_NAME_MAP[system.name]
              }`}</option>
            ) : null}
            <option value="controlhub">ControlHub</option>
          </Select>
          {system?.active && system?.integrated ? (
            <PromiseButton
              iconBefore={RefreshIcon}
              promiser={getSyncPromise}
            >{`${SYSTEMS_NAME_MAP[system.name]} Sync`}</PromiseButton>
          ) : null}
        </Pane>
        <Pane display="flex" marginY={8}>
          <Button
            disabled={!Object.values(selected).length}
            iconBefore={TrashIcon}
            marginRight={8}
            onClick={handleSelectedVendorsDelete}
          >
            Delete
          </Button>
          <Button
            iconBefore={AddIcon}
            marginLeft={8}
            onClick={handleAddNewVendor}
          >
            New vendor
          </Button>
        </Pane>
      </Pane>
      <DataTable
        columns={tableColumns}
        data={data}
        onSelect={handleRowSelect}
      />
      {dialog}
      <BlockingDialog
        onClose={handleCloseBlockingDialog}
        open={openBlockingDialog}
        blockingData={blockingData}
      />
    </Pane>
  );
};

export default AccountingVendorsScreen;
